/**
 * Centraliza as constantes para seletores CSS e nomes de classes usados
 * em todo o componente de editor de template.
 * Isso melhora a manutenibilidade, evita "magic strings" no código e
 * garante consistência entre o TypeScript e o HTML/CSS.
 */

/**
 * Nomes de classes CSS usados para estilização e gerenciamento de estado.
 */
export const CLASSES = {
  /**
   * Aplicada a um chip ou bloco quando ele está selecionado.
   */
  IS_SELECTED: 'is-selected',
  /**
   * Aplicada a um argumento quando ele está exibindo seu nome como placeholder.
   */
  IS_PLACEHOLDER: 'is-placeholder',
  /**
   * Classe base para elementos de argumento.
   */
  ARG_PLACEHOLDER: 'arg-placeholder',
  /**
   * Usada para o wrapper de cada argumento dentro de um chip.
   */
  ARG_WRAPPER: 'arg-wrapper',
  /**
   * Usada para aplicar uma animação de entrada a novos chips.
   */
  CHIP_ENTER: 'chip-enter',
  /**
   * Usada para aplicar uma animação de entrada a novos chips.
   */
  CHIP_WRAPPER: 'chip-wrapper',
  /**
   * Usada pelo elemento de tooltip do editor de template.
   */
  TOOLTIP: 'template-tooltip',
  /**
   * O cabeçalho de um bloco (ex: `{{#if condition}}`).
   */
  BLOCK_STATEMENT: 'block-statement',
  /**
   * O cabeçalho de um bloco (ex: `{{#if condition}}`).
   */
  BLOCK_HEADER: 'block-statement__header',
  /**
   * A área de conteúdo principal de um bloco.
   */
  BLOCK_PROGRAM: 'block-statement__program',
  /**
   * A área de conteúdo `{{else}}` de um bloco.
   */
  BLOCK_INVERSE: 'block-statement__inverse',
  /**
   * O tooltip do bloco
   */
  BLOCK_TOOLTIP: 'block-statement__tooltip',
} as const;

/**
 * Seletores CSS usados para consultar o DOM em busca de elementos específicos do editor.
 */
export const SELECTORS = {
  /**
   * Seleciona o contêiner de um bloco `{{#if}}`, `{{#each}}`, etc.
   */
  BLOCK_STATEMENT: `.${CLASSES.BLOCK_STATEMENT}`,
  /**
   * Seleciona o `<span>` wrapper de uma variável ou função ("chip").
   */
  CHIP_WRAPPER: `.${CLASSES.CHIP_WRAPPER}`,
  /**
   * Seleciona o botão de exclusão de um chip ou bloco.
   */
  DELETE_ACTION: '[data-action="delete"]',
  /**
   * Seleciona o `<span>` de um argumento de função/bloco.
   */
  ARG_NAME: '[data-arg-name]',
  /**
   * Seleciona um chip de variável pelo seu caminho.
   */
  PATH: '[data-path]',
  /**
   * Seleciona um chip de função pelo seu nome.
   */
  FUNCTION_NAME: '[data-function-name]',
  /**
   * Seleciona um bloco pelo nome do seu helper.
   */
  BLOCK_HELPER: '[data-block-helper]',
  /**
   * Seleciona o cabeçalho de um bloco (ex: `{{#if condition}}`).
   */
  BLOCK_HEADER: `.${CLASSES.BLOCK_HEADER}`,
  /**
   * Seleciona a área de conteúdo principal de um bloco.
   */
  BLOCK_PROGRAM: `.${CLASSES.BLOCK_PROGRAM}`,
  /**
   * Seleciona a área de conteúdo `{{else}}` de um bloco.
   */
  BLOCK_INVERSE: `.${CLASSES.BLOCK_INVERSE}`,
  /**
   * Seleciona qualquer elemento que seja editável.
   */
  CONTENT_EDITABLE: '[contenteditable="true"]',
} as const;
